<template>
    <div class="main report">

        <div class="report-heading">
            <h1><span class="breadcrumb" @click="clearFilters()">Inventory Received</span><i v-if="supplier_name" class="fas fa-chevron-right"></i>{{ supplier_name }}</h1>
            <div v-if="notEmpty(totals)" class="report-total">
                <div>
                    <label>Total Received Cost</label><br>{{ formatCurrency(totals.received_cost) }}
                </div>
                <div>
                    <label>Total Received Retail</label><br>{{ formatCurrency(totals.received_price) }}
                </div>
                <div>
                    <label>Total Margin</label><br>{{ totals.margin }}
                </div>
            </div>
        </div>

        <div class="filters with-compare">
            <div class="three-columns selectors">
                <label>Supplier</label>
                <label>UPC / SKU</label>
                <label v-if="showDealerFilter">Dealer</label>
                <label v-else></label>
                <div class="input-with-control">
                    <input class="suppliers" type="text" v-model="filters.supplier_name" placeholder="Search..." @keyup.enter="setFilter('supplier')">
                    <div v-if="filters.supplier_id" class="button input-clear" @click="setFilter('supplier', 'clear')"><i class="fas fa-times"></i></div>
                    <div class="button suppliers input-go" @click="setFilter('supplier')"><i class="fas fa-search"></i></div>
                    <div v-if="supplierSearchList.length > 0 || supplierSearchMessage" class="search-list">
                        <div v-for="supplier in supplierSearchList" class="search-list-item" :key="supplier.supplier_id" @click="setFilterFromSearch(supplier)">{{ supplier.description }}</div>
                        <div v-if="supplierSearchMessage">{{ supplierSearchMessage }}</div>
                    </div>
                </div>
                <div class="input-with-control">
                    <input class="suppliers" type="text" v-model="filters.search_text" placeholder="Search..." @keyup.enter="setFilter('search_text')">
                    <div v-if="filters.search_text" class="button input-clear" @click="setFilter('search_text', 'clear')"><i class="fas fa-times"></i></div>
                    <div class="button suppliers input-go" @click="setFilter('search_text')"><i class="fas fa-search"></i></div>
                    <div v-if="itemSearchList.length > 0" class="search-list">Message or list</div>
                </div>
                <div v-if="showDealerFilter" class="input-with-control">
                    <input class="suppliers" type="text" v-model="filters.dealer" placeholder="Search..." @keyup.enter="setFilter('location')">
                    <div class="button suppliers input-go" @click="setFilter('location')"><i class="fas fa-search"></i></div>
                    <div v-if="dealerSearchList.length > 0 || dealerSearchMessage" class="search-list">
                        <div v-for="dealer in dealerSearchList" class="search-list-item" :key="dealer.location_id" @click="setFilterFromSearch(location)">{{ dealer.description }}</div>
                        <div v-if="dealerSearchMessage">{{ dealerSearchMessage }}</div>
                    </div>
                </div>
                <div v-else></div>
            </div>
            <div>
                <div class="input-with-control date-control">
                    <label>Start</label>
                    <label>End</label>
                    <label></label>
                    <input class="suppliers" type="date" :min="minFilterDate" :max="maxFilterDate" v-model="filters.start_date" required="required">
                    <input class="suppliers" type="date" :min="minFilterDate" :max="maxFilterDate" v-model="filters.end_date" required="required">
                    <div class="button suppliers input-go" @click="setFilterDates()"><i class="fas fa-calendar-check"></i></div>
                </div>
            </div>
            <div>
                <div class="button suppliers compare-button" @click="showCompare = true"><i class="fas fa-calendar-plus"></i></div>
            </div>
        </div>

        <div v-if="message" class="empty-list">
            {{ message }}
            <div v-if="loading" class="loading">
                <i class="fas fa-spinner fa-spin"></i>
            </div>
        </div>

        <div v-if="suppliers.length > 0" class="report-list suppliers">
            <div class="report-row heading">
                <h2>Supplier</h2>
                <label></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'total_received' }]" @click="setSort('total_received')">Items Received<span v-html="showSort('total_received')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'received_cost' }]" @click="setSort('received_cost')">Cost of<br>Goods Received<span v-html="showSort('received_cost')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'received_price' }]" @click="setSort('received_price')">Retail<br>Price Received<span v-html="showSort('received_price')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'profit' }]" @click="setSort('profit')">Profit<span v-html="showSort('profit')"></span></label>
                <label class="right">Margin</label>
                <label class="right">% of Total<br>Rcvd Retail</label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'average_item_price' }]" @click="setSort('average_item_price')">Average<br>Item Price<span v-html="showSort('average_item_price')"></span></label>
            </div>
            <div class="report-row" v-for="(data, index) in suppliers" :key="index">
                <div><span class="row-title" :style="getUser.user_type_code == 'SUPERADMIN' || getUser.user_type_code == 'ADMIN' ? 'cursor: pointer;' : ''" @click="editSupplier(data)">{{ data.description }}</span></div>
                <div><div class="button suppliers" @click="getListItems(data)"><i class="fas fa-list"></i></div></div>
                <div><span class="numbers full">{{ data.total_received || 0 }}</span></div>
                <div><span class="numbers full">{{ formatCurrency(data.received_cost) || 0 }}</span></div>
                <div><span class="numbers full">{{ formatCurrency(data.received_price) || 0 }}</span></div>
                <div><span class="numbers full">{{ formatCurrency(data.profit) || 0 }}</span></div>
                <div><span class="numbers full">{{ data.margin }}</span></div>
                <div><span class="numbers full">{{ percentOfTotal(data) }}</span></div>
                <div><span class="numbers full">{{ data.average_item_price }}</span></div>
            </div>
            <pagination v-if="showPagination" :paging="paging" @updatePage="goToPage"></pagination>
         </div>
        <div v-if="!loading && suppliers.length == 0 && items.length == 0" class="empty-list">This list is empty</div>

        <div v-if="items.length > 0" class="report-list">
            <div class="report-row supplier-items heading">
                <h2>Item</h2>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'total_received' }]" @click="setSort('total_received')">Items Received<span v-html="showSort('total_received')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'received_cost' }]" @click="setSort('received_cost')">Cost of<br>Goods Received<span v-html="showSort('received_cost')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'received_price' }]" @click="setSort('received_price')">Retail<br>Price Received<span v-html="showSort('received_price')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'profit' }]" @click="setSort('profit')">Profit<span v-html="showSort('profit')"></span></label>
                <label class="right">Margin</label>
                <label class="right">% of Total<br>Rcvd Retail</label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'average_item_price' }]" @click="setSort('average_item_price')">Average<br>Item Price<span v-html="showSort('average_item_price')"></span></label>
            </div>
            <div class="report-row supplier-items" v-for="(data, index) in items" :key="index">
                <div>
                    <span class="row-title">{{ data.description }}</span>
                    <div class="two-columns row-controls">
                        <div><label>UPC</label>{{ data.upc }}</div>
                        <div><div class="button suppliers" @click="getListItems(data)"><i class="fas fa-list"></i></div></div>
                    </div>
                </div>
                <div><span class="numbers full">{{ data.total_received || 0 }}</span></div>
                <div><span class="numbers full">{{ formatCurrency(data.received_cost) || 0 }}</span></div>
                <div><span class="numbers full">{{ formatCurrency(data.received_price) || 0 }}</span></div>
                <div><span class="numbers full">{{ formatCurrency(data.profit) || 0 }}</span></div>
                <div><span class="numbers full">{{ data.margin }}</span></div>
                <div><span class="numbers full">{{ percentOfTotal(data) }}</span></div>
                <div><span class="numbers full">{{ averageItemPrice(data) }}</span></div>
            </div>
            <pagination v-if="showDetailPagination" :paging="detail_paging" @updatePage="goToDetailPage"></pagination>

        </div>

        <div v-if="showModal" class="modal">
            <div class="inner">
                <h2>Edit This Supplier</h2>
                <div class="content">
                    <label>Supplier Name</label>
                    <input type="text" v-model="editingSupplierData.description">
                    <label>Merge this supplier with others like it...</label>
                    <div class="input-with-control">
                        <input class="suppliers" type="text" v-model="supplierSearchText" placeholder="Search..." @keyup.enter="searchSuppliers()">
                        <div class="button suppliers input-go" style="height: 2.44em; padding: 0.66em 0;" @click="searchSuppliers()"><i class="fas fa-search"></i></div>
                        <table v-if="mergeSupplierList.length > 0" class="modal-search-list">
                            <tr v-for="supplier in mergeSupplierList" :key="supplier.supplier_id">
                                <td><input type="checkbox" v-model="supplier.selected"></td>
                                <td>{{ supplier.supplier_name }}</td>
                                <td>{{ supplier.client_id > 1 ? 'Dealer ' + supplier.client_id : 'System' }}</td>
                            </tr>
                        </table>
                    </div>
                </div>
                <div class="modal-controls">
                    <div class="button cancel" @click="showModal = false">Cancel</div>
                    <div class="button suppliers" @click="saveSupplier()">Save</div>
                    <div v-if="editSupplierMessage" class="message">{{ editSupplierMessage }}</div>
                </div>
            </div>
        </div>

        <compare-dates :active="showCompare" :section="'suppliers'" @cancel="showCompare = false" />

    </div>
</template>
<script>
export default {
    name: 'suppliers',
    props: ['supplier_id'],
    data() {
        return {
            loading: true,
            message: '',
            filters: {
                section: 'suppliers',
                start_date: '',
                end_date: '',
                page: 1,
                supplier_id: '',
                supplier_name: '',
                search_text: '',
                location_id: '',
                location_name: '',
                filter_by_list: '',
                sort_column: 'total_received',
                sort_descending: true,
                detail_page: 1
            },
            totals: {},
            paging: {},
            detail_paging: {},
            suppliers: [],
            items: [],
            supplier_name: '',
            supplierSearchList: [],
            supplierSearchMessage: '',
            itemSearchList: [],
            itemSearchMessage: '',
            dealerSearchList: [],
            dealerSearchMessage: '',
            showModal: false,
            editSupplierMessage: '',
            supplierSearchText: '',
            editingSupplierData: {},
            mergeSupplierList: [],
            minFilterDate: '',
            maxFilterDate: '',
            showCompare: false
        }
    },
    created() {
        // any filters previously stored for this section?
        if (this.getFilters.section == 'suppliers') {
            this.filters = this.getFilters // filters were stored last time we hit fetchDataItems()
        }
        this.filters.start_date = this.getQueryString('start') || this.getYearStartDate()
        this.filters.end_date = this.getQueryString('end') || this.getYesterdaysDate()
        this.minFilterDate = this.filters.start_date.split('-').shift() + '-01-01'
        this.maxFilterDate = this.filters.end_date.split('-').shift() + '-12-31'
        if (this.supplier_id) {
            this.filters.supplier_id = this.supplier_id
            this.fetchDataSupplier()
        } else {
            this.fetchData()
        }
    },
    computed: {
        showPagination() {
            return this.paging.total_pages > 1
        },
        showDetailPagination() {
            return this.detail_paging.total_pages > 1
        },
        showDealerFilter() {
            return false; // this.getUser && this.getUser.user_type_code && this.getUser.user_type_code.indexOf('ADMIN') > -1
        }
    },
    methods: {
		fetchData() {
            this.supplier_name = ''
            this.search_text = ''
            this.totals = {}
            this.suppliers = []
            this.loading = true
            this.message = 'Loading'
            let apiAction = 'get_report_suppliers'
            if (this.filters.start_date != this.getYearStartDate()) {
                this.message = this.message + ' custom date report'
            }
            this.apiCall({ destination: apiAction, data: this.filters }).then(obj => {
                if (obj.status == 'success') {
                    this.storeFilters(this.filters) // store current filter set
                    window.scrollTo(0,0);
                    this.items = []
                    this.suppliers = obj.suppliers
                    this.paging = obj.paging
                    this.totals = obj.totals
                    this.loading = false
                    this.message = ''
                } else {
                    this.message = obj.message
                }
            })
        },
        setFilterDates() {
            this.$router.push({ path: this.$route.path , query: { start: this.filters.start_date, end: this.filters.end_date } })
        },
        showSort(column) {
            if (this.filters.sort_column == column) {
                if (this.filters.sort_descending) {
                    return "<i class='fas fa-caret-down'></i>"
                } else {
                    return "<i class='fas fa-caret-up'></i>"
                }
            } else {
                return "&nbsp;"
            }
        },
        setSort(which) {
            if (this.loading) { return }
            if (this.filters.sort_column == which) {
                this.filters.sort_descending = !this.filters.sort_descending
            } else {
                this.filters.sort_column = which
            }
            this.setFilter('sort')
        },
        goToPage(page) {
			this.paging.current_page = page
			this.setFilter('page')
		},
        goToDetailPage(page) {
			this.detail_paging.current_page = page
			this.setFilter('detail_page')
        },
        setFilter(which, action) {
            switch (which) {
                case 'page':
                    this.filters.page = this.paging.current_page
                    this.fetchData()
                    return false;
                case 'detail_page':
                    this.filters.detail_page = this.detail_paging.current_page
                    this.fetchDataSupplier()
                    return false;
                case 'supplier':
                    this.supplierSearchList = []
                    this.supplierSearchMessage = ''
                    if (action == 'clear') {
                        this.filters.supplier_id = ''
                        if (this.filters.search_text) {
                            this.fetchDataSupplier()
                        } else {
                            this.fetchData()
                        }
                    } else {
                        this.searchFilter('supplier')
                    }
                    return false
                case 'search_text':
                    if (action == 'clear') {
                        this.filters.search_text = ''
                        if (this.filters.supplier_id) {
                            this.fetchDataSupplier()
                        } else {
                            this.fetchData()
                        }
                    } else {
                        this.fetchDataSupplier()
                    }
                    return false
                case 'location':
                    this.dealerSearchMessage = 'Dealer search coming soon'
                    setTimeout(() => { this.dealerSearchMessage = '' }, 2000)
                    return false
            }
            if (this.supplier_id) {
                this.filters.supplier_id = this.supplier_id
                this.fetchDataSupplier()
            } else {
                this.fetchData()
            }
        },
        clearFilters() {
            if (this.supplier_id) {
                this.filters.detail_page = 1
                this.supplier_name = ''
            } else {
                this.filters = {
                    section: 'suppliers',
                    start_date: '',
                    end_date: '',
                    page: 1,
                    detail_page: 1,
                    supplier_id: '',
                    supplier_name: '',
                    search_text: '',
                    location_id: '',
                    location_name: '',
                    filter_by_list: ''
                }
            }
            this.filters.start_date = this.getQueryString('start') || this.getYearStartDate()
            this.filters.end_date = this.getQueryString('end') || this.getYesterdaysDate()
            this.storeFilters(this.filters) // store empty filter set
            this.message = ''
            if (this.$route.path != '/suppliers') {
                // pushing route will clear id
                this.$router.push({path: '/suppliers', query: this.$route.query})
            } else {
                // but if base route already, reload data
                this.fetchData()
            }
        },
        searchFilter(which) {
            this.filters.filter_by_list = which
            this.apiCall({ destination: 'get_filter_list', data: this.filters }).then(obj => {
                if (obj.status == 'success') {
                    if (obj.filter_list.length == 1) {
                        this.filters.supplier_name = obj.filter_list[0]['description']
                        this.filters.supplier_id = obj.filter_list[0]['supplier_id']
                        this.$router.push('/suppliers/' + this.filters.supplier_id)
                    } else if (obj.filter_list.length > 1) {
                        this.supplierSearchList = obj.filter_list
                    } else {
                        this.supplierSearchMessage = 'Supplier not found'
                    }
                } else {
                    this.message = obj.message
                }
            })
        },
        setFilterFromSearch(data) {
            if (this.filters.filter_by_list == 'supplier') {
                this.supplierSearchList = []
                this.supplierSearchMessage = ''
                this.filters.supplier_name = data.description
                this.filters.supplier_id = data['supplier_id']
            }
            if (this.filters.filter_by_list == 'location') {
                this.dealerSearchList = []
                this.dealerSearchMessage =  ''
                this.filters.location_name = data.description
                this.filters.location_id = data['location_id']
            }
            this.fetchDataSupplier()
        },
        getListItems(data) {
            if (data.supplier_id) {
                this.$router.push({path: '/suppliers/' + data.supplier_id, query: this.$route.query})
            } else if (data.product_id) {
                this.$router.push({path: '/item/suppliers/' + data.product_id, query: this.$route.query})
            }
        },
        fetchDataSupplier() {
            this.totals = {}
            this.suppliers = []
            this.items = []
            this.loading = true
            this.message = 'Loading'
            let apiAction = 'get_report_suppliers_supplier'
            if (this.filters.start_date != this.getYearStartDate()) {
                this.message = this.message + ' custom date report'
            }
            this.apiCall({ destination: apiAction, data: this.filters }).then(obj => {
                this.loading = false
                if (obj.status == 'success') {
                    this.storeFilters(this.filters) // store current filter set
                    window.scrollTo(0,0);
                    this.supplier_name = obj.supplier_name
                    this.items = obj.items
                    this.detail_paging = obj.paging
                    this.totals = this.getTotals
                    this.message = ''
                } else {
                    this.message = obj.message
                }
            })
        },
        percentOfTotal(data) {
            return ((Number(data.received_price) / this.totals.received_price) * 100).toFixed(1)
        },
        averageItemPrice(data) {
            return this.formatCurrency(Number(data.received_price) / Number(data.total_received))
        },
        editSupplier(data) {
            if (this.getUser.user_type_code == 'SUPERADMIN' || this.getUser.user_type_code == 'ADMIN') {
                this.editingSupplierData = data
                this.editSupplierMessage = ''
                this.supplierSearchText = ''
                this.mergeSupplierList = []
                this.showModal = true
            }
        },
        searchSuppliers() {
            if (!this.supplierSearchText) {
                return
            }
            this.mergeSupplierList = []
            this.apiCall({ destination: 'get_list_suppliers', data: { search_text: this.supplierSearchText, exclude_supplier_id: this.editingSupplierData.supplier_id } }).then(obj => {
                if (obj.status == 'success') {
                    if (obj.suppliers.length > 0) {
                        this.mergeSupplierList = obj.suppliers
                    } else {
                        this.editSupplierMessage = 'No matching suppliers found'
                    }
                } else {
                    this.editSupplierMessage = obj.message
                }
            })
        },
        saveSupplier() {
            var supplierList = []
            if (this.mergeSupplierList.length > 0) {
                for (var i = 0; i < this.mergeSupplierList.length; i++) {
                    if (this.mergeSupplierList[i]['selected']) {
                        supplierList.push(this.mergeSupplierList[i].supplier_id)
                    }
                }
            }
            this.apiCall({ destination: 'save_item_supplier', data: { supplier_data: this.editingSupplierData, merge_list: supplierList } }).then(obj => {
                if (obj.status == 'success') {
                    this.fetchData() // reacquire the list
                    this.showModal = false
                } else {
                    this.editSupplierMessage = obj.message
                }
            })
        }
    }
}
</script>
<style lang="scss" scoped>
.report-row {
    grid-template-columns: 2.5fr 2em 0.5fr 1fr 1fr 1fr 0.75fr 0.75fr 0.75fr;
}
.supplier-items {
    grid-template-columns: 2fr 0.5fr 1fr 1fr 1fr 0.75fr 0.75fr 0.75fr;
}
.row-controls {
    grid-template-columns: 3fr 1fr;
    label { width: auto; padding: 0 0.5em 0 0.5em; }
}
</style>
